import * as actions from './game.actions'
import * as audioActions from './AudioContainer/audio.actions'
import * as videoPlayerActions from '../VideoPlayer/videoPlayer.actions'
import * as Errors from '../Errors'

import DeviceInfo from '../DeviceInfo'

const deviceInfo = new DeviceInfo().toObject()
const initialState = {
  changingScene: false,
  currentError: null,
  currentSceneId: null,
  lastSceneId: null,
  device: deviceInfo,
  currentMediaId: null,
  nextSceneId: null,
  popups: [],
  promptFullscreen:
    deviceInfo.info.device.type == 'mobile' || deviceInfo.info.device.type == 'tablet',
  resizing: false,
  screens: [],
  audiosPlaying: {},
  audioDisabled: false,
  lastTouchedTarget: null,
  showConsumeWarning: true,
  showingLoginError: false,
  showingSceneTransition: true,
  state: {
    conditions: {},
  },
}

const actionsMap = {
  // Add one audio to the stack
  [actions.ADD_AUDIO]: (state, action) => {
    const audioId = action.data

    if (state.audiosPlaying[audioId]) {
      return state
    }
    let audiosPlaying = Object.assign({}, state.audiosPlaying)
    audiosPlaying[audioId] = {}
    return {
      ...state,
      audiosPlaying,
    }
  },
  [audioActions.DISABLE_AUDIO]: (state, action) => {
    let bool = action.data

    return {
      ...state,
      audioDisabled: bool,
    }
  },
  // Add one popup to the stack
  [actions.ADD_POPUP]: (state, action) => {
    const popupId = action.data
    const popupIndex = state.popups.findIndex((popup) => popup === popupId)

    if (popupIndex !== -1) {
      return state
    }

    const popups = state.popups.slice()
    popups.splice(state.popups.length, 0, popupId)

    return {
      ...state,
      popups,
    }
  },

  // Add one screen to the stack
  [actions.ADD_SCREEN_FINISH]: (state, action) => {
    const screenId = action.data
    const screenIndex = state.screens.findIndex((screen) => screen === screenId)

    if (screenIndex !== -1) {
      return state
    }

    const screens = state.screens.slice()
    screens.splice(state.screens.length, 0, screenId)

    return {
      ...state,
      screens,
    }
  },

  [actions.CLEAR_ALL_SCREENS]: (state) => ({
    ...state,
    screens: [],
  }),

  [actions.CLEAR_ALL_POPUPS]: (state) => ({
    ...state,
    popups: [],
  }),
  // Signal touch current target
  [actions.UNIVERSAL_TOUCH_START]: (state, action) => {
    const data = action.data

    return {
      ...state,
      lastTouchedTarget: data.id,
    }
  },

  // Scene change finish
  [actions.CHANGE_SCENE_FINISH]: (state, action) => {
    let lastSceneId =
      action.data !== state.currentSceneId ? state.currentSceneId : state.lastSceneId
    return {
      ...state,
      changingScene: false,
      lastSceneId: lastSceneId,
      currentSceneId: action.data,
      nextSceneId: null,
    }
  },

  // Show the error component
  [actions.HIDE_ERROR]: (state) => {
    return {
      ...state,
      currentError: null,
      showingError: false,
      showingSceneTransition: false,
    }
  },

  // Hide scene transition
  [actions.HIDE_SCENE_TRANSITION]: (state) => ({
    ...state,
    showingSceneTransition: false,
  }),
  // pause one audio to avoid multiple sounds
  [actions.PAUSE_AUDIO]: (state, action) => {
    const audioId = action.data

    if (!state.audiosPlaying[audioId]) {
      return state
    }
    const audiosPlaying = Object.assign({}, state.audiosPlaying)
    audiosPlaying[audioId].paused = true
    return {
      ...state,
      audiosPlaying,
    }
  },
  // Request fullscreen only one
  [actions.PROMPT_FULLSCREEN]: (state) => ({
    ...state,
    promptFullscreen: false,
  }),

  // Register components from other modules
  [actions.REGISTER_COMPONENTS]: (state, action) => ({
    ...state,
    components: { ...state.components, ...action.data },
  }),

  // Registe the audio
  [actions.REGISTER_AUDIO_PLAYING]: (state, action) => {
    const audio = action.data
    const audiosPlaying = Object.assign({}, state.audiosPlaying)
    audiosPlaying[audio.props.id] = audio

    return {
      ...state,
      audiosPlaying,
    }
  },

  // Signal window resizing finialization
  [actions.RESIZE_FINISH]: (state) => ({
    ...state,
    resizing: false,
  }),

  // Signal window resizing start
  [actions.RESIZE_START]: (state) => ({
    ...state,
    resizing: true,
  }),

  // resume one audio paused
  [actions.RESUME_AUDIO]: (state, action) => {
    const audioId = action.data

    const audiosPlaying = Object.assign({}, state.audiosPlaying)
    audiosPlaying[audioId].paused = false
    return {
      ...state,
      audiosPlaying,
    }
  },
  // Remove one audio from the stack
  [actions.REMOVE_AUDIO]: (state, action) => {
    const audioId = action.data
    const currentAudio = state.audiosPlaying[audioId]

    const audiosPlaying = Object.assign({}, state.audiosPlaying)
    if (currentAudio) {
      audiosPlaying[audioId] = undefined
      delete audiosPlaying[audioId]
    }

    return {
      ...state,
      audiosPlaying,
    }
  },

  // Remove one popup from stack
  [actions.REMOVE_POPUP]: (state, action) => {
    const popupId = action.data
    const popupIndex = state.popups.findIndex((popup) => popup === popupId)

    if (popupIndex === -1) {
      return state
    }

    const popups = state.popups.slice()
    popups.splice(popupIndex, 1)

    return {
      ...state,
      popups,
    }
  },

  // Remove one screen to the stack
  [actions.REMOVE_SCREEN_FINISH]: (state, action) => {
    const screenId = action.data
    const screenIndex = state.screens.findIndex((screen) => screen === screenId)

    if (screenIndex === -1) {
      return state
    }

    const screens = state.screens.slice()
    screens.splice(screenIndex, 1)

    return {
      ...state,
      screens,
    }
  },

  // Set device Info
  [actions.SET_DEVICE_INFO]: (state, action) => {
    const allowedDevices = ['desktop', 'tablet', 'mobile']
    const deviceInfo = action.data
    const { type } = deviceInfo.info.device

    if (!allowedDevices.some((device) => device === (type || 'desktop').toLowerCase())) {
      return {
        ...state,
        device: deviceInfo,
        currentError: Errors.UNSUPPORTED_DEVICE_ERROR,
        showingError: true,
        showingSceneTransition: false,
      }
    }

    return {
      ...state,
      device: deviceInfo,
    }
  },

  [actions.SET_SCREENS]: (state, action) => ({
    ...state,
    screens: action.data,
  }),

  // Show the error component
  [actions.SHOW_ERROR]: (state, action) => ({
    ...state,
    currentError: action.data,
    showingError: true,
    showingSceneTransition: false,
  }),

  // Show scene transition
  [actions.SHOW_SCENE_TRANSITION]: (state) => ({
    ...state,
    showingSceneTransition: true,
  }),

  // Sync with savepoint
  [actions.SAVEPOINT_SYNC_GAME]: (state, action) => {
    const savePoint = action.data
    const transitionState = {
      currentSceneId: savePoint.currentSceneId,
      lastSceneId: savePoint.lastSceneId,
      showConsumeWarning: savePoint.showConsumeWarning,
    }

    return {
      ...state,
      ...transitionState,
    }
  },

  // toggle consume warning
  [actions.TOGGLE_CONSUME_WARNING]: (state, action) => ({
    ...state,
    showConsumeWarning: action.data,
  }),

  // Set current media id to be displayed
  [videoPlayerActions.SHOW_MEDIA]: (state, { data }) => ({
    ...state,
    currentMediaId: data,
  }),

  // Finish media playback
  [videoPlayerActions.FINISH_MEDIA]: (state) => ({
    ...state,
    currentMediaId: null,
  }),

  // Set Cursor Position
  [actions.SET_CURSOR_POSITION]: (state, { data }) => ({
    ...state,
    cursorPosition: { x: data.clientX, y: data.clientY },
  }),
}

/* eslint require-jsdoc: 0 */
export function reducer(state = initialState, action = {}) {
  const fn = actionsMap[action.type]
  return fn ? fn(state, action) : state
}
